// Port of api/fuel/app/classes/filter/package/games.php
// JS note:
// - this file is the top-level package orchestrator for Games entities
// - the PHP side spreads more of this behaviour across package type/entity classes
// - the JS wrapper exists to wire those units into the offline engine without moving response shape into controllers
import F from 'offline/filter/constants'
import DB from 'offline/db'
import Util from 'offline/util'
import GameEntity from 'offline/filter/package/games/game'
import PlayEntity from 'offline/filter/package/games/play'
import RatingEntity from 'offline/filter/package/games/rating'
import CollectionEntity from 'offline/filter/package/games/collection'

var ENTITY_BY_PACKAGE = {}
ENTITY_BY_PACKAGE[F.PACKAGE_GAME] = GameEntity
ENTITY_BY_PACKAGE[F.PACKAGE_PLAY] = PlayEntity
ENTITY_BY_PACKAGE[F.PACKAGE_RATING] = RatingEntity
ENTITY_BY_PACKAGE[F.PACKAGE_COLLECTION] = CollectionEntity

function unique(array) {
	var seen = {}
	var result = []

	for (var i = 0; i < array.length; i++) {
		var value = parseInt(array[i], 10)
		if (!value || seen[value]) continue
		seen[value] = true
		result.push(value)
	}

	return result
}

function sortUnits(units) {
	return (units || []).slice().sort(function(a, b) {
		return (a.priority || 0) - (b.priority || 0)
	})
}

function runDataUnits(entities, config, filter, data, global) {
	for (var i = 0; i < entities.length; i++) {
		var units = sortUnits(entities[i].data_units)

		for (var j = 0; j < units.length; j++) {
			var unit = units[j]
			if (unit.require && !unit.require(config, filter, data, global)) continue
			var result = unit.run(filter, data, config, global)
			if (result !== undefined) global = result
		}
	}

	return global
}

function keyedBy(rows, field) {
	var result = {}
	for (var i = 0; i < rows.length; i++) result[rows[i][field]] = rows[i]
	return result
}

export default {
	run: function(config, filter, data, global) {
		global = this.prepare(config, filter, data, global)

		var entities = []
		for (var i = 0; i < (config.packageType || []).length; i++) {
			var entity = ENTITY_BY_PACKAGE[config.packageType[i]]
			if (entity) entities.push(entity)
		}

		global = runDataUnits(entities, config, filter, data, global)

		var results = []
		var resultIndex = data.results || []
		for (var ri = 0; ri < resultIndex.length; ri++) {
			var resultId = resultIndex[ri]
			var item = this.packageItem(resultId, entities, global, config, filter)
			if (item) results.push(item)
		}

		var nested = {}
		var nestedKeys = Object.keys(global.nestedIndex || {})
		for (var ni = 0; ni < nestedKeys.length; ni++) {
			var nestedId = parseInt(nestedKeys[ni], 10)
			var nestedItem = this.packageItem(nestedId, entities, global, config, filter)
			if (nestedItem) nested[nestedId] = nestedItem
		}

		return [results, { nested: nested }]
	},

	prepare: function(config, filter, data, global) {
		var resultIds = unique(data.results || [])
		var nestedIds = unique(data.nested || [])
		var gameIds = []
		var resultRows = {}
		var resultGameMap = data.mapIndex || {}

		global.results = global.results || {}
		global.collections = global.collections || {}
		global.nestedIndex = global.nestedIndex || {}

		if (!resultIds.length && !nestedIds.length) return global

		if (config.resultType === F.RESULT_PLAY) {
			var playRows = DB.queryFast('SELECT * FROM play WHERE play_id IN(' + Util.in(resultIds) + ')')
			resultRows = keyedBy(playRows, 'play_id')

			for (var pi = 0; pi < playRows.length; pi++) {
				resultGameMap[playRows[pi].play_id] = playRows[pi].game_id
				gameIds.push(playRows[pi].game_id)
			}
		} else if (config.resultType === F.RESULT_RATING) {
			var ratingRows = DB.queryFast('SELECT * FROM rating WHERE rating_id IN(' + Util.in(resultIds) + ')')
			resultRows = keyedBy(ratingRows, 'rating_id')

			for (var rai = 0; rai < ratingRows.length; rai++) {
				resultGameMap[ratingRows[rai].rating_id] = ratingRows[rai].game_id
				gameIds.push(ratingRows[rai].game_id)
			}
		} else if (config.resultType === F.RESULT_COLLECTION) {
			// Page results — fetch unconditionally.
			var pageCollectionRows = DB.queryFast('SELECT * FROM collection WHERE collection_id IN(' + Util.in(resultIds) + ')')

			// Nested children — filter to ONLY those whose nested-parent is in
			// the current page's result index. Mirrors the PHP behaviour at
			// filter/package/games.php:39-58 (the JS port previously fetched
			// every nested child of every result across the entire collection,
			// which on alan how meant ~2,100 nested rows in `data.nested`
			// instead of the page-relevant ~12 — 3.3 MB of wire bloat).
			var nestedCollectionRows = []
			if (nestedIds.length && resultIds.length) {
				var resultIn = Util.in(resultIds)
				var nestedIn = Util.in(nestedIds)
				nestedCollectionRows = DB.queryFast(
					'SELECT * FROM collection WHERE collection_id IN(' + nestedIn + ')' +
					' AND (' +
						'(collection_nested = 1 AND collection_nested_official IN(' + resultIn + '))' +
						' OR ' +
						'(collection_nested = 2 AND collection_nested_custom IN(' + resultIn + '))' +
					')'
				)
			}

			var collectionRows = pageCollectionRows.concat(nestedCollectionRows)
			var collections = keyedBy(collectionRows, 'collection_id')

			for (var ci = 0; ci < collectionRows.length; ci++) {
				var collection = collectionRows[ci]
				resultGameMap[collection.collection_id] = collection.game_id
				gameIds.push(collection.game_id)
				global.collections[collection.collection_id] = collection
			}

			// Mark nested-page rows in the global nested index. Only the rows
			// that survived the page filter are considered nested for this
			// page; the rest of nestedIds are no-ops here.
			for (var nci = 0; nci < nestedCollectionRows.length; nci++) {
				global.nestedIndex[nestedCollectionRows[nci].collection_id] = true
			}

			// Walk the (possibly filtered) collection set in order: page
			// results first, then page-nested children. The orchestrator
			// later separates them via global.nestedIndex.
			for (var crk = 0; crk < resultIds.length; crk++) {
				var rid = resultIds[crk]
				if (collections[rid]) resultRows[rid] = collections[rid]
			}
			for (var nci2 = 0; nci2 < nestedCollectionRows.length; nci2++) {
				var nid = nestedCollectionRows[nci2].collection_id
				if (collections[nid]) resultRows[nid] = collections[nid]
			}
		} else {
			gameIds = resultIds.slice()

			for (var gi = 0; gi < resultIds.length; gi++) {
				resultGameMap[resultIds[gi]] = resultIds[gi]
				resultRows[resultIds[gi]] = { game_id: resultIds[gi] }
			}

			if (nestedIds.length) {
				var relationRows = DB.queryFast(
					'SELECT game_relation_parent, game_relation_child FROM game_relation WHERE game_relation_child IN(' + Util.in(nestedIds) + ') AND game_relation_parent IN(' + Util.in(resultIds) + ')'
				)

				for (var gri = 0; gri < relationRows.length; gri++) {
					var relation = relationRows[gri]
					resultGameMap[relation.game_relation_child] = relation.game_relation_child
					resultRows[relation.game_relation_child] = { game_id: relation.game_relation_child }
					global.nestedIndex[relation.game_relation_child] = true
					gameIds.push(relation.game_relation_child)
				}
			}
		}

		gameIds = unique(gameIds)
		global.resultGameMap = resultGameMap
		global.gameIds = gameIds
		global.results = resultRows

		if (gameIds.length) {
			global.games = Util.keyed_array(
				DB.queryFast('SELECT * FROM game WHERE game_id IN(' + Util.in(gameIds) + ')'),
				'game_id'
			)
		}

		global.ownerGroup = (config.resultType === F.RESULT_COLLECTION && !(filter.status && filter.status.merge))
			? 'collection_id'
			: 'game_id'

		if (gameIds.length && global.userIndex.length) {
			var ownerRows = DB.queryFast(
				'SELECT * FROM collection WHERE game_id IN(' + Util.in(gameIds) + ') AND user_id IN(' + Util.in(global.userIndex) + ')'
			)

			global.owners = {}
			for (var oi = 0; oi < ownerRows.length; oi++) {
				var owner = ownerRows[oi]
				var ownerKey = global.ownerGroup === 'collection_id' ? owner.collection_id : owner.game_id
				if (!global.owners[ownerKey]) global.owners[ownerKey] = []
				global.owners[ownerKey].push(owner)
			}
		}

		return global
	},

	local: function(resultId, global) {
		var result = global.results ? global.results[resultId] : null
		var gameId = global.resultGameMap ? global.resultGameMap[resultId] : resultId

		return {
			id: resultId,
			result: result || null,
			game_id: gameId,
			game: global.games ? global.games[gameId] : null,
			context: false
		}
	},

	packageItem: function(resultId, entities, global, config, filter) {
		var local = this.local(resultId, global)
		if (!local.game) return null

		var item = { id: resultId }
		for (var i = 0; i < entities.length; i++) {
			var result = entities[i].run(resultId, local, global, config, filter)
			if (!result) continue

			var keys = Object.keys(result)
			for (var j = 0; j < keys.length; j++) {
				if (keys[j] === 'id') continue
				item[keys[j]] = result[keys[j]]
			}
		}

		return item
	}
}
